import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
export default function Header() {
  Alpine.data("header", () => ({
    activeDropdown: null,
    mobileOpen: false,
    activeMobileSection: null,
    langOpen: false,

    // ─── Language selector ─────────────────────────────────────────
    languages: [],
    currentLang: "",

    init() {
      this._closeTimer = null;
      this.$watch("mobileOpen", (val) => {
        document.body.style.overflow = val ? "hidden" : "";
      });

      if (typeof Weglot !== "undefined") {
        Weglot.on("initialized", () => this._initLangs());
        Weglot.on("languageChanged", (newLang) => {
          this.currentLang = newLang;
          this._sortLangs();
        });
        if (Weglot.getCurrentLang()) this._initLangs();
      }

      gsap.set(this.$refs.activeline, { opacity: 0, width: 0 });

      const activeLink = this.$el.querySelector("nav a[aria-current='page']");
      this._activePageLi = activeLink ? activeLink.closest("li") : null;

      this.onScrollAnimation();
      this.animateIn();
      this.initLangAnimation();
    },
    animateIn() {
      var tl = gsap.timeline({
        defaults: {
          ease: "power3.out",
          duration: 0.45,
        },
        paused: true,
        onComplete: () => {
          const target = this._activePageLi;
          if (!target) return;
          const line = this.$refs.activeline;
          const containerRect = line.parentElement.getBoundingClientRect();
          const liRect = target.getBoundingClientRect();
          gsap.set(line, {
            x: liRect.left - containerRect.left,
            width: liRect.width,
          });
          gsap.to(line, { opacity: 1, duration: 0.3, ease: "power2.out" });
        },
      });
      // tl.fromTo(
      //   this.$refs.bar.children[0],
      //   {
      //     yPercent: 100,
      //   },
      //   {
      //     yPercent: 0,

      //     transformOrigin: "top",
      //   },
      // );

      // tl.fromTo(
      //   this.$refs.bar.children[0].children[0].children,
      //   {
      //     opacity: 0,
      //     y: -10,
      //     // scaleY: 2,
      //   },
      //   {
      //     opacity: 1,
      //     y: 0,

      //     stagger: 0.06,
      //   },
      //   "-=50%",
      // );

      setTimeout(() => {
        tl.play();
      }, 150);
    },
    onScrollAnimation() {
      ScrollTrigger.create({
        onUpdate: (self) => {
          if (self.direction > 0 && self.progress * self.end > 100) {
            gsap.to(this.$refs.bar, {
              y: "-150%",
              duration: 0.35,
              ease: "power4.out",
            });
          } else {
            gsap.to(this.$refs.bar, {
              y: "0%",
              duration: 0.35,
              ease: "power4.out",
            });
          }
        },
      });
    },

    _initLangs() {
      const from = Weglot.options.language_from;
      const destinations = (Weglot.options.languages || []).map((l) =>
        typeof l === "string" ? l : (l.language_to ?? l.code_iso_639_1),
      );
      this.languages = [from, ...destinations].map((code) => ({
        code,
        name: Weglot.getLanguageName(code),
      }));
      this.currentLang = Weglot.getCurrentLang();
      this._sortLangs();
    },

    _sortLangs() {
      this.languages.sort((a) => (a.code === this.currentLang ? -1 : 1));
    },

    switchLang(code) {
      this.closeLang();

      // Weglot.switchTo() does a soft, JS-driven swap which was causing
      // issues on the frontpage. Instead, build the /{langcode}/ URL
      // ourselves and do a real full page load.
      const from = Weglot.options.language_from;
      const codes = this.languages.map((l) => l.code);
      const segments = window.location.pathname.split("/").filter(Boolean);

      // Strip any existing language segment (e.g. "da" in /da/about).
      if (segments.length && codes.includes(segments[0])) {
        segments.shift();
      }

      const rest = segments.join("/");
      const isHome = segments.length === 0;

      // The frontpage needs a trailing slash on its language segment
      // (e.g. "/da/") — other pages don't.
      const path = isHome
        ? code === from
          ? "/"
          : `/${code}/`
        : code === from
          ? `/${rest}`
          : `/${code}/${rest}`;

      window.location.href = `${path}${window.location.search}${window.location.hash}`;
    },

    moveActiveLine(li) {
      const line = this.$refs.activeline;
      const containerRect = line.parentElement.getBoundingClientRect();
      const liRect = li.getBoundingClientRect();

      gsap.to(line, {
        x: liRect.left - containerRect.left,
        width: liRect.width,
        opacity: 1,
        duration: 0.6,
        ease: "power3.out",
      });
    },

    hideActiveLine() {
      if (this._activePageLi) {
        this.moveActiveLine(this._activePageLi);
      } else {
        gsap.to(this.$refs.activeline, {
          opacity: 0,
          duration: 0.4,
          ease: "power3.out",
        });
      }
    },

    openDropdown(id) {
      clearTimeout(this._closeTimer);
      this.activeDropdown = id;

      const leftOffset = this.$el.getBoundingClientRect().left;
      this.$root.style.setProperty("--activeMenuLeftOffset", `${leftOffset}px`);
    },

    closeDropdown() {
      this._closeTimer = setTimeout(() => {
        this.activeDropdown = null;
      }, 100);
    },

    // Close dropdown when focus moves outside the header bar entirely.
    handleFocusOut(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.closeDropdown();
      }
    },

    toggleMobile() {
      this.mobileOpen = !this.mobileOpen;
      if (!this.mobileOpen) this.activeMobileSection = null;
    },

    openMobileSection(id) {
      this.activeMobileSection = this.activeMobileSection === id ? null : id;
    },

    initLangAnimation() {
      const { langBackdrop, langPanel, langBox } = this.$refs;

      gsap.set(langPanel, { display: "none" });
      gsap.set(langBackdrop, { autoAlpha: 0 });
      gsap.set(langBox, { y: 40, opacity: 0 });

      this._langTl = gsap
        .timeline({
          paused: true,
          onReverseComplete: () => gsap.set(langPanel, { display: "none" }),
        })
        .set(langPanel, { display: "flex" })
        .to(
          langBackdrop,
          { autoAlpha: 1, duration: 0.3, ease: "power2.out" },
          0,
        )
        .to(
          langBox,
          { y: 0, opacity: 1, duration: 0.35, ease: "power2.out" },
          0.1,
        );
    },

    toggleLang() {
      this.langOpen = !this.langOpen;
      this.langOpen ? this._langTl.play() : this._langTl.reverse();
    },

    closeLang() {
      this.langOpen = false;
      this._langTl.reverse();
    },

    closeAll() {
      this.activeDropdown = null;
      this.langOpen = false;
      this.mobileOpen = false;
      this.activeMobileSection = null;
    },
  }));
}
